import { mathFix, isAccountingZero } from "../services/mathServices"
import { getSymbolData } from "./invoiceProductServices"
import { cloneDeep } from "lodash"

type Header = {
    title: string;
    value: string;
    sortable?: boolean;
    align: "start" | "center" | "end";
    width?: string;
    symbol?: string
}

type Tag = {
    name: string
    id: string
}

type Balance = {
    amountSigned:number
    tag: Tag
    tagType: string
    data: any
}

type Product = {
    id: string
    title: string
    reference: string
    tag: Tag
    quantity: number,
    quantityName: string,
    quantityType: string,
    type: string,
}

type ExpenseDistributions = {
    secondaryIndirectChargeDistributions
    indirectExpenseDistributions
}

export class StockAssessment {
    // input
    balances: Balance[] = []
    products: Product[] = []
    //
    analysisCenterTable = []
    amountKeys = {}
    expenseTable = []
    productTable = []
    // constant
    secondaryTagName = "CI/Administration"
    directExpenseType = "directExpense"
    secondaryExpenseType = "secondaryExpense"
    constructor(invoiceProducts, balances) {
        if (balances?.length) {
            for (const balance of balances) {
                this.balances.push({
                    tag: balance.tag ? {id:balance.tag.id, name:balance.tag.name}: undefined,
                    amountSigned: balance.amountSigned,
                    tagType: balance.tagType || balance.objectTagProductType,
                    data: balance.objectTagData
                })
            }
        }
        if (invoiceProducts?.length) {
            for (const product of invoiceProducts) {
                if (!product.tag) {
                    continue
                }
                this.products.push({
                    id: product.id,
                    tag: {id:product.tag.id, name: product.tag.name},
                    type: product.type,
                    title: product.title,
                    reference: product.reference,
                    quantity: product.quantity,
                    quantityName: product.quantityName,
                    quantityType: product.quantityType
                })
            }
        }
    }

    computeTables() {
        this.computeAnalysisCenterTable()
        this.computeExpenseTable()
        this.computeProductTable()
    }

    //
    // Analysis Center
    //

    get analysisCenterHeaders() {
        if (!this.balances?.length) {
            return []
        }
        const headers: Header[] = [{ title: 'Charge', value: 'title', align: 'start' }]
        if (this.balances?.length) {
            let addUnclassified = false
            let addDirectExpense = false
            let addSecondaryExpense = false
            for (const balance of this.balances) {
                if (balance.tag) {
                    if (balance.tagType === "EXPENSE_DIRECT") {
                        addDirectExpense = true
                    } else if (!headers.find(h => h.value === balance.tag.id) && balance.tagType?.includes("PRIMARY") ) {
                        headers.push({ title: balance.tag.name , value: balance.tag.id, align: 'end' })
                    }
                    if (balance.tagType?.includes("SECONDARY")) {
                        addSecondaryExpense = true
                    }
                } else {
                    addUnclassified = true
                }
            }
            if (addDirectExpense) {
                headers.push({ title: "Charges directes" , value: this.directExpenseType, align: 'end' })
            }
            if (addSecondaryExpense) {
                headers.splice(1,0,{ title: "Total" , value: this.secondaryExpenseType, align: 'center' })
            }
            if (addUnclassified) {
                headers.push({ title: "Non Classées" , value: "unclassifed", align: 'end' })
            }
        }
        return headers
    }

    computeAnalysisCenterTable() {
        this.analysisCenterTable = []
        this.amountKeys = {}
        if (!this?.balances?.length) {
            return
        }

        const directExpenseRow = {
            title: "Coûts directs",
            type: "directExpense",
            tagId: undefined,
            directExpense: 0,
            values: {},
            distributionKeys: undefined
        }

        const primaryCenterRow = {
            title: "Répartition primaire",
            type: "primaryExpense",
            tagId: undefined,
            values: {},
            distributionKeys: undefined
        }
        const secondaryCenterRow = {
            title: "Répartition secondaire",
            type: "secondaryDistributions",
            tagId: undefined,
            values: undefined,
            distributionKeys: undefined
        }
        const unclassifedRow = {
            title: "Non classée",
            type: "unclassified",
            tagId: undefined,
            unclassifed: 0,
            values: {},
            distributionKeys: undefined
        }
        const analysisCenterTable = [directExpenseRow, primaryCenterRow, secondaryCenterRow]
        const secondaryTables = []

        const secondaryTags = this.tags.filter(tag => tag.tagType?.includes("SECONDARY"))
        for(const sTag of secondaryTags) {
            let otherSecondaryCenterRow = {
                title: sTag.name,
                type: 'secondaryExpense',
                tagId: sTag.id,
                values: {},
                distributionKeys: {},
                secondaryExpense: 0
            }
            secondaryTables.push(otherSecondaryCenterRow)
        }
        // debugger
        for (const balance of this.balances) {
            if (balance.tag) {
                if(balance.tagType === "EXPENSE_DIRECT") {
                    directExpenseRow.directExpense += balance.amountSigned
                } else if (balance.tagType?.startsWith("EXPENSE_INDIRECT")){
                    this.amountKeys[balance.tag.id] = true
                    if (primaryCenterRow.values[balance.tag.id]) {
                        primaryCenterRow.values[balance.tag.id] += balance.amountSigned
                    } else {
                        primaryCenterRow.values[balance.tag.id] = balance.amountSigned
                    }
                    if (balance.tagType?.includes("SECONDARY")) {
                        const otherSecondaryCenterRow = secondaryTables.find(t => t.tagId === balance.tag.id && t.type === 'secondaryExpense')
                        if (otherSecondaryCenterRow) {
                            if (otherSecondaryCenterRow.secondaryExpense) {
                                otherSecondaryCenterRow.secondaryExpense += balance.amountSigned
                            } else {
                                otherSecondaryCenterRow.secondaryExpense = balance.amountSigned
                            }
                        }
                    } else {
                        for(const row of secondaryTables) {
                            row.distributionKeys[balance.tag.id] = 1
                        }
                    }
                }
            } else {
                unclassifedRow.unclassifed += balance.amountSigned
            }
        }

        analysisCenterTable.push(...secondaryTables)

        analysisCenterTable.push({
            title: "Total",
            type: "total",
            tagId: undefined,
            distributionKeys: undefined,
            values: {}
        })

        for(const sTag of secondaryTags) {
            let total = 0.0
            const data = sTag.data?.stocks ? sTag.data.stocks : {}
            // debugger
            const secondaryCenterRow = secondaryTables.find(t => t.tagId === sTag.id)
            let count = Object.keys(secondaryCenterRow.distributionKeys).length
            for (const key in secondaryCenterRow.distributionKeys) {
                if (data[key]) {
                    total += this.toDecimal(data[key])
                    secondaryCenterRow.distributionKeys[key] = this.toDecimal(data[key])
                    count --
                }
            }
            for (const key in secondaryCenterRow.distributionKeys) {
                if (data[key]) {
                    continue
                } else {
                    let distribution = mathFix(1.0 / count)
                    if (1 - (total + distribution) < 2.e-2) {
                        distribution = mathFix(1.0 - total)
                    }
                    secondaryCenterRow.distributionKeys[key] = distribution
                    total += distribution
                }
            }
        }
        
        this.onDistributionKeyChanged_(analysisCenterTable)
        this.analysisCenterTable = analysisCenterTable
    }

    onDistributionKeyChanged_(analysisCenterTable) {
        const secondaryRows = analysisCenterTable.filter(r => r.type === 'secondaryExpense')
        for(const row of secondaryRows) {
            if (row.distributionKeys && row.values) {
                for (const key in row.distributionKeys) {
                    row.values[key] = row.secondaryExpense * row.distributionKeys[key]
                }
            }
        }

        const totalCenterRow = analysisCenterTable.find(r => r.type === "total")
        if (totalCenterRow) {
            for (const key in this.amountKeys) {
                totalCenterRow.values[key] = 0
            }
            for (const row of analysisCenterTable) {
                if (row === totalCenterRow) {
                    continue
                }
                if (row.directExpense) {
                    totalCenterRow.values.directExpense = row.directExpense
                }
                if (row.values) {
                    for (const key in this.amountKeys) {
                        if (row.values[key]) {
                            totalCenterRow.values[key] += row.values[key]
                        }
                    }
                }
            }
        }
    }

    onDistributionKeyChanged() {
        this.onDistributionKeyChanged_(this.analysisCenterTable)
    }

    //
    // expense distribution
    //

    getHeaders(type){
        const headers: Header[] = [
            { title: "Charge", value: "title", align: "start", symbol:undefined },
            { title: "Total", value: "value", align: "start", symbol:undefined }
        ]

        const tags = type === "EXPENSE" ? this.expenseTags : this.productTags
        for (const tag of tags) {
            headers.push({ title: tag.name, value: tag.id, align: "end", symbol: tag.symbol })
        }
        return headers
    }

    get expenseHeaders() {
        return this.getHeaders("EXPENSE")
    }

    get productHeaders() {
        return this.getHeaders("PRODUCT")
    }

    getTypedTags(type) {
        const tags = []
        if (this.products.length) {
            for (const invoiceProduct of this.products) {
                if (invoiceProduct.type !== type) {
                    continue
                }
                if (!invoiceProduct.tag) {
                    continue
                }
                if (tags.find(t => t.id === invoiceProduct.tag.id)) {
                    continue
                }
                const symbolData = getSymbolData(invoiceProduct)
                const symbol = symbolData?.symbolTitle || symbolData?.symbol
                tags.push({...invoiceProduct.tag, symbol})
            }
        }
        return tags
    }

    get expenseTags() {
        return this.getTypedTags("EXPENSE")
    }

    get productTags() {
        return this.getTypedTags("PRODUCT")
    }

    get tags() {
        const tags = []
        for (const balance of this.balances) {
            if (balance.tag && !tags.find(tag => tag.id === balance.tag.id)) {

                tags.push({
                    type: balance.tag.name.indexOf("Approvisionnement") === -1 ? "PRODUCT" : "EXPENSE",
                    data: balance.data,
                    tagType: balance.tagType,
                    ...balance.tag
                })
            }
        }
        tags.push({id: this.directExpenseType, type: this.directExpenseType, name: "Charges directes"})
        return tags
    }

    getTable(type) {
        const table = []
        const totalCenterRow = this.analysisCenterTable.find(r => r.type === "total")
        if (totalCenterRow) {
            for (const key in totalCenterRow.values) {
                const tag = this.tags.find(tag => tag.id === key)
                if (!isAccountingZero(totalCenterRow.values[key])) {
                    if (tag.tagType !== type) {
                        continue
                    }
                    const tableRow = {
                        title: tag?.name ? tag.name : '',
                        type: tag.tagType,
                        tagId: tag?.id || undefined,
                        value: totalCenterRow.values[key],
                        products:[]
                    }

                    const tags = type?.includes("EXPENSES") ? this.expenseTags : this.productTags
                    for (const tag of tags) {
                        tableRow.products.push({
                            type,
                            tag,
                            percentage: 0.0,
                            value: 0.0
                        })
                    }

                    let count = tableRow.products.filter(p => p.type === tableRow.type).length

                    if (count) {
                        // debugger
                        const tagData = tag.data?.stocks ? tag.data.stocks : undefined
                        if(tagData) {
                            const ids = Object.keys(tagData);
                            for (const productDistribution of tableRow.products) {
                                const index = ids.findIndex(id => id === productDistribution.tag.id)
                                if (index > -1) {
                                    const percentage = this.toDecimal(tagData[productDistribution.tag.id])
                                    if(percentage) {
                                        productDistribution.percentage = percentage
                                        count--
                                        productDistribution.value = tableRow.value * productDistribution.percentage
                                    }
                                }
                            }
                        }
                    }

                    table.push(tableRow)
                }
            }
        }
        return table
    }

    computeExpenseTable() {
        this.expenseTable = this.getTable("EXPENSE_INDIRECT_PRIMARY_ON_EXPENSES")
    }

    computeProductTable() {
        this.productTable = this.getTable("EXPENSE_INDIRECT_PRIMARY_ON_PRODUCTS")
    }

    onPercentageChanged(row, data=undefined) {
        for(const product of row.products) {
            if(data) {
                product.percentage = this.toDecimal(data[product.tag.id]) || 0
            }
            product.value = row.value * product.percentage
        }
    }

    updateExpenseAndProductTables() {
        const totalCenterRow = this.analysisCenterTable.find(r => r.type === "total")
        for(const row of this.expenseTable) {
            row.value = totalCenterRow.values[row.tagId]
            this.onPercentageChanged(row)
        }
        for(const row of this.productTable) {
            row.value = totalCenterRow.values[row.tagId]
            this.onPercentageChanged(row)
        }
    }

    toDecimal(value) {
        if (typeof value !== 'number') return 0;
        return value > 1 && value <= 100 ? value / 100 : value;
    };

}
